/**
 * AI Art Director — rules for a GENERATED Design JSON V2 document.
 *
 * `rules.js` judges the Creative Plan: was the thinking worth building? These
 * rules judge what the model actually built. A plan can be excellent and the
 * document that came back from it thin, generic, or flat — only reading the
 * document catches that.
 *
 * Every rule here exists because it names a way the old V1 engine failed:
 * six-node compositions, placeholder-only copy, emoji standing in for icons,
 * a single flat fill, no decoration, no focal point. A document that trips
 * these is not a design, and it must not reach an operator as if it were.
 *
 * Pure and deterministic, like every other rule set in this module. It reads a
 * document and returns findings; it never edits one.
 */

import { SEVERITY } from './reviewSchema';
import { expectationFor, SUPPORTING_ROLES } from './compositionExpectations';
import { isUnusableSrc } from '../assets/assetIntent';

/**
 * Narrowest a resolved HERO photograph may be.
 *
 * Mirrors MIN_HERO_WIDTH in services/image_search.py, where ranking prefers it.
 * Ranking only prefers; this rule is what makes it visible when no candidate
 * met the bar and the best available was used anyway.
 */
const MIN_HERO_ASSET_WIDTH = 1200;

const asArray = (v) => (Array.isArray(v) ? v : []);
const asObject = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});

/** Every node in the document, flattened through groups and frames. */
export const allNodes = (document) => {
  const out = [];
  const walk = (node) => {
    if (!node || typeof node !== 'object') return;
    out.push(node);
    asArray(node.children).forEach(walk);
    if (node.child) walk(node.child);
  };
  asArray(asObject(document).pages).forEach((page) => {
    asArray(asObject(page).sections).forEach((section) => {
      asArray(asObject(section).nodes).forEach(walk);
    });
  });
  return out;
};

/** Every section across every page. */
export const allSections = (document) =>
  asArray(asObject(document).pages).flatMap((page) => asArray(asObject(page).sections));

const objectsOfType = (nodes, type) => nodes.filter((n) => n.node === 'object' && n.type === type);

/** Copy that says nothing — the tell of a template that was filled, not written. */
const PLACEHOLDER_COPY = /^(lorem|ipsum|placeholder|sample|your (text|title|headline|logo|company)|text here|title here|headline|subheading|body text|tagline|company name|lorem ipsum)\b/i;

/**
 * Pictographs in COPY. Deliberately narrow: an en dash or a middle dot in real
 * copy is typography, not decoration.
 */
const EMOJI_IN_TEXT = /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}]/u;

/**
 * A real icon is NAMED — "phone", "tooth", "calendar-check". Anything that is
 * not a name is a character being used as a picture, whether it is an emoji
 * (🦷) or a geometric symbol (⬢, ▲, ★). The first real generation returned
 * three identical U+2B22 hexagons as its "feature icons", which the earlier
 * emoji-only check did not see.
 */
const ICON_NAME = /^[a-z0-9][a-z0-9 _-]*$/i;

/** An image source that was never a real image. */
const PLACEHOLDER_SRC = /^(path\/to\/|\.{0,2}\/?(images?|assets|img)\/|example\.com|your-|placeholder|todo|tbd)/i;
const USABLE_SRC = /^(https:\/\/|\{\{[A-Za-z0-9_]+\}\}$)/;

/**
 * A document below this many nodes is not a composition. The old engine's
 * six-node output is the reason this is a blocker rather than a warning.
 */
export const MIN_NODES = 12;

/** Fewer distinct type tokens than this and the typography has no hierarchy. */
export const MIN_TYPE_STYLES = 3;

/**
 * Below this the canvas was authored in millimetres or points, not pixels.
 * No real pixel canvas for a printable document is narrower than this.
 */
export const MIN_CANVAS_PX = 600;

/** Sections carrying real content (decoration alone is not structure). */
/** Type tokens that read as the lead of a composition. */
const LEAD_TYPE_STYLES = new Set(['display', 'h1']);

const CONTENTFUL_ROLES = new Set([
  'hero', 'header', 'content', 'offer', 'features', 'gallery', 'pricing',
  'testimonial', 'stats', 'cta', 'contact', 'footer',
]);


// ─── Visual rules ────────────────────────────────────────────────────────────
//
// These read the RENDERED page, not the Design JSON. Everything above judges
// what the model described; these judge what it looks like. A browser QA found
// flyers scoring 100/100 with a headline smaller than their own footer and a
// third of the canvas empty — neither is visible in a document that carries no
// geometry, which is why the earlier rules could not have caught them.
//
// Each is skipped when no rendered page was supplied, so a caller reviewing a
// document alone gets the structural verdict and no false visual findings.

/** True when the caller gave us geometry to judge. */
const hasVisuals = (ctx) => Boolean(ctx.visuals);

// ── Imagery resolution ────────────────────────────────────────────────
//
// The design model decides what imagery a page needs; a provider finds the real
// photograph. These rules check that the second half actually happened. Without
// them a design can pass every structural and visual test while the preview
// shows an empty box where the hero photograph was supposed to be — which is
// exactly what browser QA kept finding.

/** True when the caller ran the image flow and told us what it found. */
const hasAssets = (ctx) => Boolean(ctx.assets);

const asAssetArray = (v) => (Array.isArray(v) ? v : []);

/** Rendered elements still carrying something that is not a real image. */
const unusableImages = (ctx) => contentImages(ctx).filter((el) => isUnusableSrc(el.src));

const contentImages = (ctx) => {
  const pages = (ctx.assets && asAssetArray(ctx.assets.pages)) || [];
  return pages.flatMap((p) => asAssetArray(p && p.elements))
    .filter((el) => el && (el.type === 'image' || el.type === 'frame'));
};

const IMAGERY_RULES = Object.freeze([
  {
    id: 'required-imagery-unresolved',
    reviews: ['Visual Narrative', 'Craft'],
    detects: 'Required imagery did not resolve',
    severity: SEVERITY.BLOCKER,
    scores: ['creative', 'premium', 'storytelling', 'marketplace'],
    check: (ctx) => {
      if (!hasAssets(ctx)) return { issue: false };
      const missing = asAssetArray(ctx.assets.unresolved).filter((u) => u && u.required);
      if (!missing.length) return { issue: false };
      const reason = missing[0].reason === 'service-unavailable'
        ? 'the image service is not configured or could not be reached'
        : `nothing matched ${missing.map((m) => `"${(m.triedQueries || []).slice(-1)[0] || m.id}"`).join(', ')}`;
      return { issue: true, detail: reason };
    },
    weakness: (ctx, detail) => `The design asks for imagery it never received — ${detail}. The page cannot carry its message without it.`,
    revision: () => 'Restate the hero imagery with a plainer, more searchable subject — everyday words a stock photo library would tag — or redesign the composition so it does not depend on a photograph.',
    strength: null,
  },
  {
    id: 'placeholder-image-remains',
    reviews: ['Craft', 'Marketplace Value'],
    detects: 'A placeholder survived into the preview',
    severity: SEVERITY.BLOCKER,
    scores: ['premium', 'marketplace', 'creative'],
    check: (ctx) => {
      if (!hasAssets(ctx)) return { issue: false };
      const bad = unusableImages(ctx);
      return { issue: bad.length > 0, detail: bad.map((el) => `${el.id}="${el.src}"`).join(', ') };
    },
    weakness: (ctx, detail) => `A placeholder is still on the page — ${detail}. An operator would be shown a grey box, not a design.`,
    revision: () => 'Every image node must carry an assetIntent the provider can search. Never emit a literal URL, a path, or a {{TOKEN}} as an image source.',
    strength: null,
  },
  {
    id: 'hero-image-too-small-for-role',
    reviews: ['Craft'],
    detects: 'Resolved hero image is too small',
    severity: SEVERITY.MAJOR,
    scores: ['premium', 'marketplace'],
    check: (ctx) => {
      if (!hasAssets(ctx)) return { issue: false };
      const heroes = contentImages(ctx).filter((el) => {
        const meta = (el.designV2 && el.designV2.resolvedAsset) || null;
        const role = `${(el.designV2 && el.designV2.sectionRole) || ''}`.toLowerCase();
        return meta && role === 'hero';
      });
      const weak = heroes.filter((el) => (el.designV2.resolvedAsset.width || 0) < MIN_HERO_ASSET_WIDTH);
      return {
        issue: weak.length > 0,
        detail: weak.map((el) => `${el.id} is ${el.designV2.resolvedAsset.width}px wide, needs ${MIN_HERO_ASSET_WIDTH}px`).join(', '),
      };
    },
    weakness: (ctx, detail) => `The hero photograph will not hold its size — ${detail}. It softens the moment the page is printed or scaled.`,
    revision: () => 'Ask for a wider subject, or give the hero a smaller declared role in the composition.',
    strength: null,
  },
]);

const VISUAL_RULES = Object.freeze([
  {
    id: 'hero-image-not-a-focal-mass',
    reviews: ['Visual Narrative', 'Craft'],
    detects: 'Declared hero image is not a focal mass',
    severity: SEVERITY.BLOCKER,
    scores: ['creative', 'premium', 'storytelling', 'marketplace'],
    // `no-focal-mass` asks whether ANYTHING anchors the page, and a wide CTA
    // button answers yes. This asks the different question: the design said it
    // leads with a photograph, so does that photograph actually lead?
    check: (ctx) => {
      if (!hasVisuals(ctx) || !ctx.expectation.imagery) return { issue: false };
      const hero = ctx.visuals.heroImage;
      if (!hero) return { issue: false };
      const need = ctx.expectation.focal.hero;
      return {
        issue: hero.share < need,
        detail: `it covers ${Math.round(hero.share * 100)}% of the canvas, needs ${Math.round(need * 100)}%`,
      };
    },
    weakness: (ctx, detail) => `The hero image is too small to function as a focal mass — ${detail}. It reads as an illustration beside the text, not the thing the page is built around.`,
    revision: (ctx) => `Hero image is too small to function as a focal mass. Redesign the composition around a substantially larger hero image — at least ${Math.round(ctx.expectation.focal.hero * 100)}% of the canvas — while preserving the existing campaign message. Give its section autoLayout sizing "fill" with a "stack" layout, and set the image node's own sizing to "fill".`,
    strength: null,
  },
  {
    id: 'cta-outweighs-the-lead',
    reviews: ['Hierarchy Strategy', 'Visual Narrative'],
    detects: 'CTA outweighs the focal point',
    severity: SEVERITY.MAJOR,
    scores: ['creative', 'communication', 'storytelling'],
    check: (ctx) => {
      if (!hasVisuals(ctx)) return { issue: false };
      const cta = ctx.visuals.cta;
      if (!cta) return { issue: false };
      const ceiling = ctx.expectation.focal.ctaMax;
      const focal = ctx.visuals.focal;
      const overFocal = focal && focal.id !== cta.id && cta.share > focal.share;
      return {
        issue: cta.share > ceiling || overFocal,
        detail: overFocal
          ? `the action block covers ${Math.round(cta.share * 100)}% against a focal mass of ${Math.round(focal.share * 100)}%`
          : `the action block covers ${Math.round(cta.share * 100)}%, ceiling is ${Math.round(ceiling * 100)}%`,
      };
    },
    weakness: (ctx, detail) => `The call to action outshouts what it is asking about — ${detail}. The eye lands on the button before it has been told what is on offer.`,
    revision: (ctx) => `The CTA must be prominent but SUBORDINATE to the focal point. Shrink the action block below ${Math.round(ctx.expectation.focal.ctaMax * 100)}% of the canvas and let the hero and headline carry the composition. Keep the same wording.`,
    strength: null,
  },
  {
    id: 'no-semantic-headline',
    reviews: ['Hierarchy Strategy'],
    detects: 'No node is marked as the primary headline',
    severity: SEVERITY.MAJOR,
    scores: ['communication', 'storytelling', 'marketplace'],
    // Being the biggest text by accident is not a hierarchy decision. A named
    // role is what lets the headline survive editing, translation and reflow.
    check: (ctx) => {
      if (!hasVisuals(ctx) || !ctx.expectation.minTypeLevels) return { issue: false };
      return { issue: !ctx.visuals.semanticHeadline, detail: 'no text node carries a headline role' };
    },
    weakness: () => 'Nothing is declared as the primary headline — the largest line is only incidentally the largest. A hierarchy that is not stated does not survive an edit.',
    revision: () => 'Give the campaign headline an explicit role of "headline" on its text node, and set it at the "display" type token. Contact, phone and footer text must carry their own roles so they read as secondary.',
    strength: null,
  },
  {
    id: 'content-off-canvas',
    reviews: ['Craft'],
    detects: 'Content falls outside the page',
    severity: SEVERITY.BLOCKER,
    scores: ['creative', 'premium', 'marketplace', 'communication'],
    // A page can lose most of its content off the bottom and still satisfy
    // every other signal, because those are computed from what remains on it.
    check: (ctx) => {
      if (!hasVisuals(ctx)) return { issue: false };
      const oob = ctx.visuals.outOfBounds;
      return {
        issue: oob.count > 0,
        detail: `${oob.count} element${oob.count === 1 ? '' : 's'} (${oob.ids.join(', ')}) overflow the canvas by up to ${oob.worstOverflow}px`,
      };
    },
    weakness: (ctx, detail) => `Part of the design is not on the page — ${detail}. Whatever is off the canvas cannot be seen, printed, or edited.`,
    revision: () => 'Every element must sit inside the canvas. Check the type tokens first: "leading" is a line-height MULTIPLIER (1.0–1.6), not a pixel value — a leading of 48 makes one headline thousands of pixels tall and pushes the rest of the page off the bottom.',
    strength: null,
  },
  {
    id: 'text-over-text',
    reviews: ['Craft'],
    detects: 'Text is drawn over other text',
    severity: SEVERITY.BLOCKER,
    scores: ['creative', 'premium', 'marketplace', 'communication'],
    check: (ctx) => {
      if (!hasVisuals(ctx)) return { issue: false };
      const c = ctx.visuals.textCollisions;
      return {
        issue: c.count > 0,
        detail: c.pairs.map(([a, b]) => `${a} over ${b}`).join(', '),
      };
    },
    weakness: (ctx, detail) => `Lines of type are printed on top of each other — ${detail}. Neither is readable where they cross.`,
    revision: () => 'Give the copy the room it needs. A headline that wraps to three lines occupies three lines: shorten it, reduce its size, or give its section the height it actually requires so the content beneath starts below it.',
    strength: null,
  },
  {
    id: 'no-display-headline',
    reviews: ['Hierarchy Strategy', 'Visual Narrative'],
    detects: 'No display-scale headline',
    severity: SEVERITY.BLOCKER,
    scores: ['creative', 'communication', 'storytelling', 'marketplace'],
    check: (ctx) => {
      if (!hasVisuals(ctx)) return { issue: false };
      const want = ctx.expectation.hierarchy.display;
      if (!want) return { issue: false };
      const v = ctx.visuals;
      const ratio = v.displayRatio;
      return {
        issue: !v.primary || ratio < want,
        detail: v.primary ? `largest text is ${Math.round(ratio * 1000) / 10}% of canvas width, needs ${Math.round(want * 1000) / 10}%` : 'no text at all',
      };
    },
    weakness: (ctx, detail) => `Nothing is set at display scale — ${detail}. The page opens with no voice.`,
    revision: (ctx) => `Set the single most important line at display scale: at least ${Math.round(ctx.expectation.hierarchy.display * 100)}% of the canvas width, and let everything else step down from it.`,
    strength: (ctx) => (hasVisuals(ctx) && ctx.visuals.displayRatio >= ctx.expectation.hierarchy.display * 1.4
      ? `The headline commands the page at ${Math.round(ctx.visuals.displayRatio * 1000) / 10}% of canvas width.` : null),
  },
  {
    id: 'inverted-hierarchy',
    reviews: ['Hierarchy Strategy', 'Communication Strategy'],
    detects: 'Metadata outranks the headline',
    severity: SEVERITY.BLOCKER,
    scores: ['creative', 'communication', 'marketplace', 'premium'],
    // The failure a browser QA found: contact lines rendered larger than the
    // headline, so the eye landed on a phone number first.
    check: (ctx) => {
      if (!hasVisuals(ctx)) return { issue: false };
      const v = ctx.visuals;
      if (!v.primary || !v.topMetadata) return { issue: false };
      const need = ctx.expectation.hierarchy.overMetadata;
      return {
        issue: v.dominanceOverMetadata !== null && v.dominanceOverMetadata < need,
        detail: `"${v.primary.text}" barely leads "${v.topMetadata.text}" (${v.dominanceOverMetadata}x, needs ${need}x)`,
      };
    },
    weakness: (ctx, detail) => `The hierarchy is inverted — ${detail}. Contact and footer text must never compete with the headline.`,
    revision: () => 'Reduce the prominence of contact, footer and legal text, and raise the headline, so the eye lands on the message before the small print.',
    strength: null,
  },
  {
    id: 'weak-headline-dominance',
    reviews: ['Hierarchy Strategy'],
    detects: 'Headline does not lead',
    severity: SEVERITY.MAJOR,
    scores: ['creative', 'storytelling', 'premium'],
    check: (ctx) => {
      if (!hasVisuals(ctx)) return { issue: false };
      const v = ctx.visuals;
      if (!v.primary || !v.second) return { issue: false };
      const need = ctx.expectation.hierarchy.dominance;
      return {
        issue: v.dominanceOverNext !== null && v.dominanceOverNext < need,
        detail: `${v.dominanceOverNext}x over the next line, needs ${need}x`,
      };
    },
    weakness: (ctx, detail) => `The headline is only ${detail} — the type reads as one flat rank rather than a hierarchy.`,
    revision: () => 'Widen the gap between the headline and what supports it: a real step in size and weight, not a nudge.',
    strength: null,
  },
  {
    id: 'dead-canvas-region',
    reviews: ['Visual Narrative', 'Craft', 'Marketplace Value'],
    detects: 'Accidental empty region',
    severity: SEVERITY.MAJOR,
    scores: ['creative', 'premium', 'marketplace'],
    /**
     * Whitespace is not the fault — pooled, purposeless whitespace is.
     *
     * A run of empty bands in the MIDDLE of a composition is a hole. The same
     * emptiness at the top or bottom edge is a margin, which premium design
     * uses deliberately, so an edge run is allowed to be larger before it
     * counts against the piece.
     */
    check: (ctx) => {
      if (!hasVisuals(ctx)) return { issue: false };
      const o = ctx.visuals.occupancy;
      // The edge allowance is for whitespace that FRAMES something. Granting it
      // unconditionally let a flyer keep a dead lower third — the margin
      // exemption was doing the work of an excuse. A margin is deliberate when
      // there is a focal mass for it to sit around; without one it is just the
      // part of the page the design never reached.
      const framesAFocalMass = Boolean(ctx.visuals.focal)
        && ctx.visuals.focal.share >= ctx.expectation.focal.mass;
      const allowed = o.emptyRunAtEdge && framesAFocalMass
        ? ctx.expectation.balance.maxEmptyRun * 1.5
        : ctx.expectation.balance.maxEmptyRun;
      return {
        issue: o.emptyBandRatio > allowed,
        detail: `${Math.round(o.emptyBandRatio * 100)}% of the page sits empty in one run${o.emptyRunAtEdge ? ' at the edge' : ' through the middle'}`,
      };
    },
    weakness: (ctx, detail) => `${detail} — that reads as unfinished, not as considered space.`,
    revision: () => 'Rebalance the composition: either extend the sections into that region with real content, or make the emptiness deliberate by anchoring it against a strong focal mass.',
    strength: (ctx) => (hasVisuals(ctx) && ctx.visuals.occupancy.emptyBandRatio <= 0.17
      ? 'Content is distributed across the whole page.' : null),
  },
  {
    id: 'sparse-composition',
    reviews: ['Craft', 'Marketplace Value'],
    detects: 'Page barely covered',
    severity: SEVERITY.MAJOR,
    scores: ['premium', 'marketplace', 'creative'],
    check: (ctx) => {
      if (!hasVisuals(ctx)) return { issue: false };
      const cover = ctx.visuals.occupancy.meanCoverage;
      return {
        issue: cover < ctx.expectation.balance.minCoverage,
        detail: `mean coverage ${Math.round(cover * 100)}%, needs ${Math.round(ctx.expectation.balance.minCoverage * 100)}%`,
      };
    },
    weakness: (ctx, detail) => `The page is barely covered — ${detail}. A marketplace template fills its canvas with intent.`,
    revision: (ctx) => `Give the composition real presence for ${ctx.expectation.character}: wider masses, a larger focal subject, and content that reaches the page edges where the design calls for it.`,
    strength: null,
  },
  {
    id: 'no-focal-mass',
    reviews: ['Visual Narrative'],
    detects: 'No dominant visual mass',
    severity: SEVERITY.MAJOR,
    scores: ['creative', 'premium', 'storytelling'],
    // Only for types that are supposed to lead with something seen.
    check: (ctx) => {
      if (!hasVisuals(ctx) || !ctx.expectation.imagery) return { issue: false };
      const f = ctx.visuals.focal;
      const need = ctx.expectation.focal.mass;
      return {
        issue: !f || f.share < need,
        detail: f
          ? `largest mass covers ${Math.round(f.share * 100)}%, needs ${Math.round(need * 100)}%`
          : 'none',
      };
    },
    weakness: (ctx, detail) => `Nothing anchors the composition visually — ${detail}. The eye has only type to hold onto.`,
    revision: () => 'Introduce one dominant visual mass — a framed photograph, a full-bleed band, or a large shape treatment — that the type sits against.',
    strength: null,
  },
]);

export const DOCUMENT_RULES = Object.freeze([
  {
    id: 'thin-composition',
    reviews: ['Visual Narrative', 'Craft'],
    detects: 'Thin composition',
    severity: SEVERITY.BLOCKER,
    scores: ['creative', 'premium', 'marketplace'],
    // The floor is per document type. A business card is SUPPOSED to be spare;
    // judging it against a flyer's floor would demand filler.
    check: (ctx) => ({
      issue: ctx.nodes.length < ctx.expectation.minNodes,
      detail: `${ctx.nodes.length} nodes (a ${ctx.documentType || 'document'} needs at least ${ctx.expectation.minNodes})`,
    }),
    weakness: (ctx, detail) => `The document has only ${detail}. That is a wireframe, not a designed composition.`,
    revision: (ctx) => `Build a real composition for ${ctx.expectation.character}. Every element must carry design intent — never add elements to reach a count.`,
    strength: (ctx) => (ctx.nodes.length >= ctx.expectation.minNodes * 1.6 ? `Composition is substantial at ${ctx.nodes.length} nodes.` : null),
  },
  {
    id: 'under-composed-for-document-type',
    reviews: ['Visual Narrative', 'Marketplace Value', 'Craft'],
    detects: 'Technically valid but under-composed',
    severity: SEVERITY.MAJOR,
    scores: ['creative', 'marketplace', 'premium', 'storytelling'],
    /**
     * The rule the node count cannot express: does this piece carry the
     * SEMANTIC PARTS its type needs? A flyer that never asks for anything, a
     * menu with one section, a poster with no subject — each is a legal
     * document and none is a finished design.
     */
    check: (ctx) => {
      const e = ctx.expectation;
      const roles = new Set(ctx.sections.map((s) => s.role).filter((r) => CONTENTFUL_ROLES.has(r)));
      const typeLevels = new Set(objectsOfType(ctx.nodes, 'text').map((n) => n.style).filter(Boolean));
      const hasImagery = objectsOfType(ctx.nodes, 'image').length > 0 || ctx.nodes.some((n) => n.node === 'frame');
      const hasCta = ctx.sections.some((s) => s.role === 'cta') || ctx.nodes.some((n) => n.role === 'cta');
      const hasSupport = ctx.sections.some((s) => SUPPORTING_ROLES.includes(s.role));

      const missing = [];
      if (roles.size < e.minContentRoles) missing.push(`only ${roles.size} content section${roles.size === 1 ? '' : 's'} where ${e.minContentRoles} are needed`);
      if (typeLevels.size < e.minTypeLevels) missing.push(`only ${typeLevels.size} type level${typeLevels.size === 1 ? '' : 's'} where ${e.minTypeLevels} are needed`);
      if (e.imagery && !hasImagery) missing.push('no image or framed subject');
      if (e.cta && !hasCta) missing.push('nothing the viewer is asked to do');
      if (e.supporting && !hasSupport) missing.push('no supporting content beneath the lead');

      return { issue: missing.length > 0, detail: missing.join('; ') };
    },
    weakness: (ctx, detail) => `For a ${ctx.documentType || 'document'} this is under-composed: ${detail}.`,
    revision: (ctx) => `This should read as ${ctx.expectation.character}. It needs at least ${ctx.expectation.minNodes} nodes across at least ${ctx.expectation.minContentRoles} content-carrying sections, and it has fewer. Add REAL PARTS, each carrying content: an eyebrow or kicker above the headline, a subheadline, three or four benefit items that each pair an icon with a label AND a line of copy, a testimonial or proof point, an offer with its terms, and a footer identity. Every one of those is a node that earns its place — this is not a request for decoration or filler.`,
    strength: (ctx) => {
      const roles = new Set(ctx.sections.map((s) => s.role).filter((r) => CONTENTFUL_ROLES.has(r)));
      return roles.size > ctx.expectation.minContentRoles
        ? `Composition carries ${roles.size} content sections, beyond what a ${ctx.documentType || 'document'} requires.`
        : null;
    },
  },
  {
    id: 'flat-typography',
    reviews: ['Hierarchy Strategy', 'Craft'],
    detects: 'Flat typography',
    severity: SEVERITY.MAJOR,
    scores: ['creative', 'communication', 'premium'],
    check: (ctx) => {
      const styles = new Set(objectsOfType(ctx.nodes, 'text').map((n) => n.style).filter(Boolean));
      return { issue: styles.size < MIN_TYPE_STYLES, detail: [...styles].join(', ') };
    },
    weakness: (ctx, detail) => `Typography has no hierarchy — every text node draws from ${detail ? `too few type tokens (${detail})` : 'the same type token'}.`,
    revision: () => 'Give the type a ranked scale: a display or headline token that leads, a subhead that supports, and a body token — with real size and weight separation between them.',
    strength: (ctx) => {
      const styles = new Set(objectsOfType(ctx.nodes, 'text').map((n) => n.style).filter(Boolean));
      return styles.size >= 4 ? `Typography is ranked across ${styles.size} type tokens.` : null;
    },
  },
  {
    id: 'no-focal-point',
    reviews: ['Hierarchy Strategy', 'Visual Narrative'],
    detects: 'No focal point',
    severity: SEVERITY.MAJOR,
    scores: ['creative', 'storytelling', 'communication'],
    // What "leads" depends on the piece. A flyer leads with a hero or a framed
    // subject; a business card leads with a name set large. Demanding a hero
    // image of a card is how a card becomes a bad flyer.
    check: (ctx) => {
      const hasHero = ctx.sections.some((s) => s.role === 'hero');
      const hasLeadImage = objectsOfType(ctx.nodes, 'image').length > 0 || ctx.nodes.some((n) => n.node === 'frame');
      const hasDisplayType = objectsOfType(ctx.nodes, 'text').some((n) => LEAD_TYPE_STYLES.has(n.style));

      return { issue: ctx.expectation.imagery ? !(hasHero || hasLeadImage) : !(hasHero || hasLeadImage || hasDisplayType) };
    },
    weakness: (ctx) => (ctx.expectation.imagery
      ? 'Nothing leads the eye — there is no hero section and no dominant image or framed subject.'
      : 'Nothing leads the eye — no hero section, and nothing is set at display scale.'),
    revision: (ctx) => (ctx.expectation.imagery
      ? 'Establish one unmistakable focal point: a hero section carrying either a framed image with a stated focal point or a display-scale statement.'
      : 'Establish one unmistakable focal point — set the single most important line at display scale and let everything else step down from it.'),
    strength: (ctx) => (ctx.sections.some((s) => s.role === 'hero') ? 'A hero section carries the composition.' : null),
  },
  {
    id: 'placeholder-copy',
    reviews: ['Communication Strategy', 'Marketing Intent'],
    detects: 'Placeholder-only copy',
    severity: SEVERITY.MAJOR,
    scores: ['communication', 'marketing', 'marketplace'],
    check: (ctx) => {
      const texts = objectsOfType(ctx.nodes, 'text').map((n) => String(n.text || '').trim()).filter(Boolean);
      const placeholder = texts.filter((t) => PLACEHOLDER_COPY.test(t));
      return {
        issue: texts.length > 0 && placeholder.length / texts.length > 0.4,
        detail: placeholder.slice(0, 3).join(' / '),
      };
    },
    weakness: (ctx, detail) => `Most of the copy is placeholder text${detail ? ` ("${detail}")` : ''} — the design communicates nothing about this business.`,
    revision: () => 'Write real copy for this industry, purpose and audience. Every text node should say something a customer would actually read.',
    strength: null,
  },
  {
    id: 'emoji-as-icon',
    reviews: ['Craft', 'Originality'],
    detects: 'Emoji standing in for design',
    severity: SEVERITY.MAJOR,
    scores: ['premium', 'creative', 'marketplace'],
    check: (ctx) => {
      const offenders = ctx.nodes.filter((n) => {
        const icon = String(n.icon || '').trim();
        if (icon && !ICON_NAME.test(icon)) return true;
        return n.type === 'text' && EMOJI_IN_TEXT.test(String(n.text || ''));
      });
      return { issue: offenders.length > 0, detail: offenders.map((n) => n.icon || n.text).slice(0, 3).join(' ') };
    },
    weakness: (ctx, detail) => `Characters are standing in for visual design (${detail}). An emoji or a geometry symbol is not iconography and never reads as premium.`,
    revision: () => 'Replace every one with a NAMED icon (for example "tooth", "calendar-check", "shield"), a vector path, or a designed shape treatment.',
    strength: null,
  },
  {
    id: 'no-semantic-structure',
    reviews: ['Visual Narrative', 'Craft'],
    detects: 'No semantic structure',
    severity: SEVERITY.MAJOR,
    scores: ['creative', 'marketplace', 'storytelling'],
    check: (ctx) => {
      const roles = new Set(ctx.sections.map((s) => s.role).filter((r) => CONTENTFUL_ROLES.has(r)));
      return { issue: roles.size < 2, detail: [...roles].join(', ') };
    },
    weakness: (ctx, detail) => `The document has no narrative structure — only ${detail ? `the "${detail}" section` : 'one undifferentiated section'}.`,
    revision: () => 'Break the design into semantic sections that tell a story in order: lead, support, proof, action.',
    strength: (ctx) => {
      const roles = new Set(ctx.sections.map((s) => s.role).filter((r) => CONTENTFUL_ROLES.has(r)));
      return roles.size >= 4 ? `Narrative runs across ${roles.size} semantic sections: ${[...roles].join(' → ')}.` : null;
    },
  },
  {
    id: 'missing-cta',
    reviews: ['Marketing Intent', 'Business Intent'],
    detects: 'No call to action',
    severity: SEVERITY.MAJOR,
    scores: ['marketing', 'marketplace', 'communication'],
    check: (ctx) => ({
      issue: !ctx.sections.some((s) => s.role === 'cta' || s.role === 'contact')
        && !ctx.nodes.some((n) => n.role === 'cta'),
    }),
    weakness: () => 'The design asks the viewer to do nothing — there is no CTA section and no node carrying a CTA role.',
    revision: () => 'Add a call to action the composition actually leads to, and give it enough visual weight to be the destination.',
    strength: (ctx) => (ctx.sections.some((s) => s.role === 'cta') ? 'The composition resolves into a CTA.' : null),
  },
  {
    id: 'flat-colour',
    reviews: ['Visual Language Coherence', 'Craft'],
    detects: 'Flat colour treatment',
    severity: SEVERITY.MINOR,
    scores: ['premium', 'creative'],
    check: (ctx) => {
      const kinds = new Set(ctx.nodes.flatMap((n) => asArray(n.fills).map((f) => asObject(f).kind)).filter(Boolean));
      const pageBg = asArray(asObject(asArray(asObject(ctx.document).pages)[0]).background);
      pageBg.forEach((f) => { if (asObject(f).kind) kinds.add(asObject(f).kind); });
      return { issue: kinds.size <= 1, detail: [...kinds].join(', ') };
    },
    weakness: () => 'Colour is applied as a single flat fill kind throughout — no layering, no gradient, no texture.',
    revision: () => 'Layer the colour using the fill vocabulary, not more solids. Give page.background an ARRAY: a "linearGradient" or "radialGradient" ground, then a "noise" or "texture" pass over it at low opacity. Above that, sections may use "solid" surfaces and one accent fill. Gradient stops are { color, at } with `at` from 0 to 1.',
    strength: (ctx) => {
      const kinds = new Set(ctx.nodes.flatMap((n) => asArray(n.fills).map((f) => asObject(f).kind)).filter(Boolean));
      return kinds.size >= 3 ? `Colour is layered across ${kinds.size} fill kinds.` : null;
    },
  },
  {
    id: 'no-premium-detail',
    reviews: ['Craft', 'Marketplace Value'],
    detects: 'No premium detail',
    severity: SEVERITY.MINOR,
    scores: ['premium', 'marketplace'],
    check: (ctx) => {
      const effects = ctx.nodes.reduce((n, node) => n + asArray(node.effects).length, 0);
      const decorative = ctx.nodes.filter((n) => n.layer === 'decoration').length;
      return { issue: effects === 0 && decorative === 0, detail: `${effects} effects, ${decorative} decorative` };
    },
    weakness: () => 'Nothing lifts this off the page — no shadows, glows, blurs or decorative layer at all.',
    revision: () => 'Add restrained depth using the effect vocabulary: put effects: [{ "kind": "dropShadow", "color": "onSurface", "y": 8, "blur": 24, "opacity": 0.12 }] on the cards or the hero frame, and consider one "glow" or "grain" pass. Valid kinds are dropShadow, innerShadow, layerBlur, backdropBlur, glow, grain, overlay — any other name fails validation outright.',
    strength: (ctx) => {
      const effects = ctx.nodes.reduce((n, node) => n + asArray(node.effects).length, 0);
      return effects >= 3 ? `Premium detail is present across ${effects} effects.` : null;
    },
  },
  {
    id: 'placeholder-imagery',
    reviews: ['Craft', 'Marketplace Value'],
    detects: 'Imagery that was never real',
    severity: SEVERITY.MAJOR,
    scores: ['marketplace', 'premium', 'creative'],
    check: (ctx) => {
      const sources = ctx.nodes
        .filter((n) => n.type === 'image' || n.node === 'frame')
        .map((n) => String((n.child && n.child.src) || n.src || '').trim())
        .filter(Boolean);
      const bad = sources.filter((src) => PLACEHOLDER_SRC.test(src) || !USABLE_SRC.test(src));
      return { issue: bad.length > 0, detail: bad.slice(0, 2).join(', ') };
    },
    weakness: (ctx, detail) => `Image sources point nowhere ("${detail}") — the design will render as a broken image.`,
    revision: () => 'Every image src must be an https URL, or a {{IMAGE1}}-style placeholder token the operator fills in. A local-looking path is neither.',
    strength: null,
  },
  {
    id: 'canvas-not-in-pixels',
    reviews: ['Craft'],
    detects: 'Canvas authored in the wrong unit',
    severity: SEVERITY.BLOCKER,
    scores: ['creative', 'marketplace', 'premium'],
    check: (ctx) => {
      const pages = asArray(asObject(ctx.document).pages);
      const tooSmall = pages
        .map((p) => asObject(asObject(p).canvas))
        .filter((c) => Number.isFinite(c.width) && c.width > 0 && c.width < MIN_CANVAS_PX);
      return { issue: tooSmall.length > 0, detail: tooSmall.map((c) => `${c.width}x${c.height}`).join(', ') };
    },
    // 210x297 is A4 in MILLIMETRES. The editor canvas is pixels, so a document
    // authored in mm renders at a twentieth of its intended size with type at
    // 8px — which is what the first real generation produced.
    weakness: (ctx, detail) => `The canvas is ${detail}, which is a print measurement, not pixels. At that size the whole design is a thumbnail.`,
    revision: () => `Author the canvas in PIXELS — at least ${MIN_CANVAS_PX} wide. A4 at print resolution is 2480x3508; a standard flyer is 1080x1350.`,
    strength: null,
  },
  {
    id: 'unlayered-background',
    reviews: ['Craft'],
    detects: 'Empty background',
    severity: SEVERITY.MINOR,
    scores: ['premium', 'creative'],
    check: (ctx) => {
      const pages = asArray(asObject(ctx.document).pages);
      return { issue: pages.length > 0 && pages.every((p) => asArray(asObject(p).background).length === 0) };
    },
    weakness: () => 'Every page sits on an empty background — the design starts on nothing.',
    revision: () => 'Give the page a designed ground: a gradient, a tinted surface, or a textured field the content sits on.',
    strength: null,
  },
].concat(VISUAL_RULES).concat(IMAGERY_RULES));
